import * as yup from "yup";
import * as React from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
    DatePicker,
    FormCheckbox,
    Input,
    Select,
} from "@jhool-io/fe-components";
import {
    IClientProviderHistory,
    IEditSessionContent,
} from "../../types/clients.types";
import useToast from "../../../../hooks/useToast";
import { useEditClientSession } from "../../hooks/clients.mutations";
import {
    formatZonedTimeToUtc,
    handleFormatDatePickerValue,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import { SessionServiceType } from "../../../notes/types/notes.types";
import { RecurrenceRule } from "../../../../utils/types";
import { SessionDeliveryOption } from "../../../../utils/types/session";

type Option = {
    label: React.ReactNode;
    value: string;
};

interface EditClientSessionProps {
    // Function to call when form submit button is clicked
    onFormSubmit(): void;
    // session details of session to be edited
    sessionDetails?: IClientProviderHistory;
}

const schema = yup.object({
    service: yup.string().required("Service Type is required"),
    recurrence_rule: yup.string().required("Recurrence is required"),
    delivery_option: yup.string(),
    is_supervisor_intake_session: yup.boolean(),
    start_date: yup.string().required("First session date is required"),
    session_start_time: yup.string().required("Session start time is required"),
    session_end_time: yup
        .string()
        .required("Session end time is required")
        .test({
            name: "is-greater",
            message: "Session end time should be later than the start time",
            test: (
                value: string | undefined,
                context: {
                    parent: {
                        session_start_time: string;
                    };
                }
            ) => {
                const { session_start_time: sessionStartTime } = context.parent;
                if (!sessionStartTime || !value) {
                    return true;
                }
                const start = new Date(`1970-01-01T${sessionStartTime}:00`);
                const end = new Date(`1970-01-01T${value}:00`);
                return end > start;
            },
        }),
});

export default function EditClientSession({
    onFormSubmit,
    sessionDetails,
}: EditClientSessionProps) {
    const [startDate, setStartDate] = React.useState<Date | null>(
        sessionDetails?.first_session_date
            ? new Date(sessionDetails?.first_session_date)
            : null
    );

    const params = useParams();

    const { clientId } = params;

    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<IEditSessionContent>({
        resolver: yupResolver(schema),
        mode: "onChange",
    });

    const { toast } = useToast();

    const queryClient = useQueryClient();

    const editSession = useEditClientSession(clientId || "");

    const defaultSessionStartTime = `${
        sessionDetails?.appointment.session_start_hr != null &&
        sessionDetails?.appointment.session_start_hr < 10
            ? `0${sessionDetails?.appointment.session_start_hr}`
            : sessionDetails?.appointment.session_start_hr
    }:${
        sessionDetails?.appointment.session_start_min != null &&
        sessionDetails?.appointment.session_start_min < 10
            ? `0${sessionDetails?.appointment.session_start_min}`
            : sessionDetails?.appointment.session_start_min
    }`;

    const defaultSessionEndTime = `${
        sessionDetails?.appointment.session_end_hr != null &&
        sessionDetails?.appointment.session_end_hr < 10
            ? `0${sessionDetails?.appointment.session_end_hr}`
            : sessionDetails?.appointment.session_end_hr
    }:${
        sessionDetails?.appointment.session_end_min != null &&
        sessionDetails?.appointment.session_end_min < 10
            ? `0${sessionDetails?.appointment.session_end_min}`
            : sessionDetails?.appointment.session_end_min
    }`;

    const getServiceTypeSelectOptions = Object.values(SessionServiceType).map(
        (serviceType) => ({
            label: (
                <span className="capitalize">
                    {removeEnumUnderscore(serviceType)}
                </span>
            ),
            value: serviceType,
        })
    );

    const getRecurrenceRuleSelectOptions = Object.values(RecurrenceRule).map(
        (recurrenceRule) => ({
            label: (
                <span className="capitalize">
                    {removeEnumUnderscore(recurrenceRule)}
                </span>
            ),
            value: recurrenceRule,
        })
    );

    const getDeliverySelectOptions = Object.values(SessionDeliveryOption).map(
        (deliveryOption) => ({
            label: (
                <span className="capitalize">
                    {removeEnumUnderscore(deliveryOption)}
                </span>
            ),
            value: deliveryOption,
        })
    );

    const onSubmit = (data: IEditSessionContent) => {
        const formattedStartTime = formatZonedTimeToUtc(
            `${handleFormatDatePickerValue(data.start_date)}T${
                data.session_start_time.split(":")[0]
            }:${data.session_start_time.split(":")[1]}:00.000`
        );

        const formattedEndTime = formatZonedTimeToUtc(
            `${handleFormatDatePickerValue(data.start_date)}T${
                data.session_end_time.split(":")[0]
            }:${data.session_end_time.split(":")[1]}:00.000`
        );

        const dataToSend = {
            sessions: [
                {
                    ...data,
                    session_id: sessionDetails?.session_id || "",
                    session_start_time: formattedStartTime,
                    session_end_time: formattedEndTime,
                },
            ],
        };

        editSession.mutate(dataToSend, {
            onSuccess: async (res) => {
                await queryClient.invalidateQueries({
                    queryKey: [clientId, "provider-history"],
                });
                toast({
                    mode: "success",
                    message: res.message || "Session edited successfully",
                });
                onFormSubmit();
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not edit session at this time.",
                });
            },
        });
    };

    return (
        <form id="edit-session" onSubmit={handleSubmit(onSubmit)}>
            <div className="fg">
                <Controller
                    name="service"
                    control={control}
                    defaultValue={
                        getServiceTypeSelectOptions.find(
                            (option) => option.value === sessionDetails?.service
                        )?.value
                    }
                    render={({ field }) => (
                        <Select
                            label="Service type"
                            placeholder="Service type"
                            isSearchable
                            options={getServiceTypeSelectOptions}
                            isLongListInDialog
                            onChange={(val) => {
                                field.onChange((val as Option).value);
                            }}
                            hasError={!!errors.service}
                            errorText={errors.service?.message}
                            isRequired
                            defaultValue={getServiceTypeSelectOptions.find(
                                (option) =>
                                    option.value === sessionDetails?.service
                            )}
                        />
                    )}
                />
            </div>

            <div className="fg fg-space-between two flex">
                <Controller
                    name="delivery_option"
                    control={control}
                    defaultValue={
                        getDeliverySelectOptions.find(
                            (option) =>
                                option.value === sessionDetails?.delivery_option
                        )?.value
                    }
                    render={({ field }) => (
                        <Select
                            label="Delivery Option"
                            placeholder="Delivery Option"
                            isSearchable
                            options={getDeliverySelectOptions}
                            isLongListInDialog
                            onChange={(val) => {
                                field.onChange((val as Option).value);
                            }}
                            hasError={!!errors.delivery_option}
                            errorText={errors.delivery_option?.message}
                            defaultValue={getDeliverySelectOptions.find(
                                (option) =>
                                    option.value ===
                                    sessionDetails?.delivery_option
                            )}
                        />
                    )}
                />
                <Controller
                    name="recurrence_rule"
                    control={control}
                    defaultValue={
                        getRecurrenceRuleSelectOptions.find(
                            (option) =>
                                option.value ===
                                sessionDetails?.appointment.recurrence_rule
                        )?.value
                    }
                    render={({ field }) => (
                        <Select
                            label="Recurrence"
                            placeholder="Recurrence"
                            isSearchable
                            options={getRecurrenceRuleSelectOptions}
                            isLongListInDialog
                            onChange={(val) => {
                                field.onChange((val as Option).value);
                            }}
                            hasError={!!errors.recurrence_rule}
                            errorText={errors.recurrence_rule?.message}
                            isRequired
                            defaultValue={getRecurrenceRuleSelectOptions.find(
                                (option) =>
                                    option.value ===
                                    sessionDetails?.appointment.recurrence_rule
                            )}
                        />
                    )}
                />
            </div>
            <div className="fg">
                <Controller
                    name="start_date"
                    control={control}
                    defaultValue={sessionDetails?.first_session_date}
                    render={({ field }) => (
                        <DatePicker
                            label="Start Date"
                            onChange={(date) => {
                                field.onChange(date);
                                setStartDate(date);
                            }}
                            selected={startDate}
                            hasError={!!errors.start_date}
                            errorText={
                                errors.start_date?.type === "typeError"
                                    ? "invalid date value"
                                    : errors.start_date?.message
                            }
                            isRequired
                            popperClassName="!z-[20]"
                            popperProps={{
                                strategy: "fixed",
                                modifiers: [
                                    {
                                        name: "zIndex",
                                        options: {
                                            zIndex: 11,
                                        },
                                    },
                                ],
                            }}
                        />
                    )}
                />
            </div>
            <div className="fg fg-space-between two flex">
                <Input
                    {...register(`session_start_time`)}
                    label="Start time"
                    placeholder="Start time"
                    hasError={!!errors.session_start_time}
                    errorText={errors.session_start_time?.message}
                    type="time"
                    isRequired
                    defaultValue={defaultSessionStartTime}
                />

                <Input
                    {...register(`session_end_time`)}
                    label="End time"
                    placeholder="End time"
                    hasError={!!errors.session_end_time}
                    errorText={errors.session_end_time?.message}
                    type="time"
                    isRequired
                    defaultValue={defaultSessionEndTime}
                />
            </div>
            <div className="fg">
                <FormCheckbox
                    {...register("is_supervisor_intake_session")}
                    label="Supervisor intake session"
                    defaultChecked={
                        sessionDetails?.appointment.session_metadata
                            ?.is_supervisor_intake_session ?? false
                    }
                />
            </div>
        </form>
    );
}
